import { createHandlerChainRunner } from "@insite/client-framework/HandlerCreator";
import applyItemAttributeOverrides from "@tSurya/Handlers/Configurator/ApplyItemAttributeOverrides";
import { updateUrlWithSelections } from "@tSurya/Handlers/Configurator/ConfiguratorUrlUtils";
import { postConfigurationData } from "@tSurya/Services/ConfiguratorService";
import { ConfigurationDataResultModel, PreConfiguredStyleModel } from "@tSurya/Store/Data/ConfiguratorModels";
import { isCoverOption } from "@tSurya/Store/Data/ConfiguratorSelectors";
import { CustomHandler } from "@tSurya/Store/CustomHandlerCreator/CustomHandlerCreator";

interface ApplyPreConfiguredStyleParameter {
    styleId: string;
    onSuccess?: () => void;
    onError?: (error: string) => void;
}

type Props = {
    style: PreConfiguredStyleModel;
    mergedSelections: Record<string, string>;
    configurationResult: ConfigurationDataResultModel;
};

type HandlerType = CustomHandler<ApplyPreConfiguredStyleParameter, Props>;

export const FindStyle: HandlerType = props => {
    var styles = props.getState().SURData.configuratorStore.productConfig?.preConfiguredStyles;
    if (!styles) {
        return false;
    }
    var style = styles.find(s => s.styleId === props.parameter.styleId);
    if (!style) {
        return false;
    }
    props.style = style;
};

export const ValidateProductId: HandlerType = props => {
    var productConfig = props.getState().SURData.configuratorStore.productConfig;
    if (!productConfig?.productId) {
        var errorMessage = "No configurator product loaded";
        props.dispatch({
            type: "Custom/Configurator/FailUpdate",
            error: errorMessage,
        });
        props.parameter.onError?.(errorMessage);
        return false;
    }
};

export const DispatchBeginUpdate: HandlerType = props => {
    props.dispatch({ type: "Custom/Configurator/BeginUpdate" });
};

export const MergeSelections: HandlerType = props => {
    var configuratorStore = props.getState().SURData.configuratorStore;
    var currentSelections = configuratorStore.selections;
    var options = configuratorStore.productConfig?.options ?? [];

    // Cover selections come exclusively from the style, so a style without a contrast cover clears it.
    var coverOptionIds = new Set(options.filter(isCoverOption).map(o => o.optionId));
    var carriedSelections: Record<string, string> = {};
    Object.keys(currentSelections ?? {}).forEach(optionId => {
        if (!coverOptionIds.has(optionId)) {
            carriedSelections[optionId] = currentSelections[optionId];
        }
    });

    props.mergedSelections = {
        ...carriedSelections,
        ...props.style.selections,
    };
};

export const PostConfigurationData: HandlerType = async props => {
    var productId = props.getState().SURData.configuratorStore.productConfig!.productId;

    try {
        props.configurationResult = await postConfigurationData({
            productId,
            selections: props.mergedSelections,
        });
    } catch (e) {
        var errorMessage = e instanceof Error ? e.message : "Failed to apply style";
        props.dispatch({
            type: "Custom/Configurator/FailUpdate",
            error: errorMessage,
        });
        props.parameter.onError?.(errorMessage);
        return false;
    }
};

export const DispatchCompleteStyleUpdate: HandlerType = props => {
    props.dispatch({
        type: "Custom/Configurator/CompleteUpdate",
        selections: props.configurationResult.selections,
        configurationResult: props.configurationResult,
    });
    props.dispatch({
        type: "Custom/Configurator/SetSelectedStyleId",
        selectedStyleId: props.style.styleId,
    });
};

export const ApplyOverrides: HandlerType = props => {
    props.dispatch(applyItemAttributeOverrides({}));
};

export const SyncUrlAfterUpdate: HandlerType = props => {
    var options = props.getState().SURData.configuratorStore.productConfig?.options;
    if (options) {
        var knownOptionIds = new Set(options.map(o => o.optionId));
        updateUrlWithSelections(props.configurationResult.selections, knownOptionIds);
    }
};

export const InvokeOnSuccess: HandlerType = props => {
    props.parameter.onSuccess?.();
};

export const chain = [
    FindStyle,
    ValidateProductId,
    DispatchBeginUpdate,
    MergeSelections,
    PostConfigurationData,
    DispatchCompleteStyleUpdate,
    ApplyOverrides,
    SyncUrlAfterUpdate,
    InvokeOnSuccess,
];

const applyPreConfiguredStyle = createHandlerChainRunner(chain, "ApplyPreConfiguredStyle");
export default applyPreConfiguredStyle;
